'use client';

import React from 'react';
import { cx } from '../utils/cx';

export type ProgressVariant = 'bar' | 'ring';

export interface ProgressProps {
  variant?: ProgressVariant;
  value: number;
  max?: number;
  label?: string;
  size?: number; // ring only
  strokeWidth?: number; // ring only
  className?: string;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function safeNumber(n: unknown, fallback: number): number {
  const v = typeof n === 'number' && Number.isFinite(n) ? n : fallback;
  return v;
}

export function Progress({
  variant = 'bar',
  value,
  max = 100,
  label,
  size = 120,
  strokeWidth = 8,
  className,
}: ProgressProps) {
  const safeMax = Math.max(1, safeNumber(max, 100));
  const safeValue = clamp(safeNumber(value, 0), 0, safeMax);
  const pct = clamp((safeValue / safeMax) * 100, 0, 100);

  if (variant === 'ring') {
    const s = Math.max(24, safeNumber(size, 120));
    const sw = clamp(safeNumber(strokeWidth, 8), 2, Math.max(2, Math.floor(s / 3)));
    const radius = (s - sw) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (pct / 100) * circumference;

    return (
      <svg
        width={s}
        height={s}
        role="img"
        aria-label={label ?? `Progress: ${Math.round(pct)}%`}
        viewBox={`0 0 ${s} ${s}`}
        className={cx('text-progress', className)}
      >
        <circle
          stroke="currentColor"
          className="text-tertiary"
          fill="transparent"
          strokeWidth={sw}
          r={radius}
          cx={s / 2}
          cy={s / 2}
          opacity={0.25}
        />
        <circle
          stroke="currentColor"
          className="text-progress"
          fill="transparent"
          strokeWidth={sw}
          strokeLinecap="round"
          r={radius}
          cx={s / 2}
          cy={s / 2}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${s / 2} ${s / 2})`}
        />
      </svg>
    );
  }

  return (
    <div
      className={cx('w-full overflow-hidden rounded-full bg-tertiary ring-1 ring-secondary_alt ring-inset', className)}
      role="progressbar"
      aria-label={label}
      aria-valuenow={safeValue}
      aria-valuemin={0}
      aria-valuemax={safeMax}
      aria-valuetext={`${Math.round(pct)}%`}
    >
      <div className="h-2 rounded-full bg-progress" style={{ width: `${pct}%` }} />
    </div>
  );
}

Progress.displayName = 'Progress';
